import { useMemo } from 'react';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import {
  parseIncompletePhoneNumber,
  parsePhoneNumber as parseCompletePhoneNumber,
} from 'libphonenumber-js';
import * as yup from 'yup';

import { useLocalizationContext } from '../../context';

const US_PHONE_NUMBER_MAX_LENGTH = 10;
const US_COUNTRY_CODE = 'US';

// ────────────────────────────────────────────────────────────────────────────────

export const useJoinForm = () => {
  const { t } = useLocalizationContext();

  const requiredFirstNameMessage = t('form.first-name-required');
  const requiredLastNameMessage = t('form.last-name-required');
  const requiredEmailMessage = t('form.email-required');
  const requiredPhoneNumberMessage = t('form.phone-number-required');
  const requiredPasswordMessage = t('form.password-required');
  const invalidPhoneNumberMessage = t('form.validation-phone-number');
  const invalidPasswordLengthMessage = t('form.min-password-length');
  const invalidCheckboxValue = t('form.validation-checkbox');
  const invalidEmail = t('form.validation-email');

  const validationSchema = useMemo(
    () =>
      yup
        .object({
          firstName: yup
            .string()
            .required(requiredFirstNameMessage)
            .max(
              50,
              t('form.max-name-length', {
                fieldName: 'First',
              }),
            )
            .matches(
              /^[\sA-Za-z]+$/,
              t('form.invalid-characters', { fieldName: 'First' }),
            ),
          lastName: yup
            .string()
            .required(requiredLastNameMessage)
            .max(
              50,
              t('form.max-name-length', {
                fieldName: 'Last',
              }),
            )
            .matches(
              /^[\sA-Za-z]+$/,
              t('form.invalid-characters', { fieldName: 'Last' }),
            ),
          email: yup
            .string()
            .email(invalidEmail)
            .required(requiredEmailMessage),
          phoneNumber: yup
            .string()
            .required(requiredPhoneNumberMessage)
            .test(`test-valid-phone-number`, function (phoneNumber) {
              // eslint-disable-next-line functional/no-this-expression
              const { path, createError } = this;
              const parsedPhoneNumber = parseIncompletePhoneNumber(
                phoneNumber ?? '',
              );
              const isValidNumber =
                parsedPhoneNumber.length === US_PHONE_NUMBER_MAX_LENGTH
                  ? parseCompletePhoneNumber(
                      parsedPhoneNumber,
                      US_COUNTRY_CODE,
                    ).isValid()
                  : true;

              return (
                isValidNumber ||
                createError({ path, message: invalidPhoneNumberMessage })
              );
            })
            .matches(/^\d+$/, invalidPhoneNumberMessage)
            .min(10, invalidPhoneNumberMessage)
            .max(10, invalidPhoneNumberMessage),
          password: yup
            .string()
            .required(requiredPasswordMessage)
            .min(8, invalidPasswordLengthMessage),
          terms: yup.bool().oneOf([true], invalidCheckboxValue),
        })
        .required(),
    [
      requiredFirstNameMessage,
      t,
      requiredLastNameMessage,
      invalidEmail,
      requiredEmailMessage,
      requiredPhoneNumberMessage,
      invalidPhoneNumberMessage,
      requiredPasswordMessage,
      invalidPasswordLengthMessage,
      invalidCheckboxValue,
    ],
  );

  // ────────────────────────────────────────────────────────────────────

  const form = useForm<JoinFormData>({
    defaultValues: {
      firstName: '',
      lastName: '',
      email: '',
      phoneNumber: '',
      password: '',
      birthday: '',
      terms: false,
    },
    resolver: yupResolver(validationSchema),
    mode: 'onBlur',
  });

  // ────────────────────────────────────────────────────────────────────

  return { form };
};

// ─── TYPES ──────────────────────────────────────────────────────────────────────

export type JoinFormData = {
  readonly firstName: string;
  readonly lastName: string;
  readonly email: string;
  readonly phoneNumber: string;
  readonly password: string;
  readonly birthday: string;
  readonly terms: boolean;
  readonly grecaptcha?: string;
};
